import {
  CreateButton,
  DeleteButton,
  EditButton,
  ExportButton,
  FilterDropdown,
  getDefaultSortOrder,
  List,
  ShowButton,
  TagField,
  TextField,
  useSelect,
  useTable,
} from "@refinedev/antd";

import { Divider, Select, Space, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { IResourceComponentsProps, useExport } from "@refinedev/core";
import { ILicence, IMetadataOther, IShipco, IVessel } from "interfaces";
import React from "react";
import { getBooleanTagColor } from "constants/tagColors";
import {
  OTHER_SERVICES_BY_PRODUCT,
  OtherServiceProductGroup,
  readOtherField,
} from "components/metadata/otherServiceCatalog";

const isProductActive = (
  group: OtherServiceProductGroup,
  others: IMetadataOther | null | undefined,
): boolean =>
  group.services.some(
    (svc) => readOtherField<string>(others, `${svc.prefix}Status`) === "active",
  );

const productGroup = (name: string): OtherServiceProductGroup => {
  const g = OTHER_SERVICES_BY_PRODUCT.find((x) => x.product === name);
  if (!g) throw new Error(`Unknown other-service product: ${name}`);
  return g;
};

const ROLLUP_PRODUCTS: Array<{ title: string; key: string; width: number }> = [
  { title: "Standard Library Publications", key: "standardLibraryPublications", width: 85 },
  { title: "eMariner Planning Station", key: "planningStation", width: 85 },
  { title: "eMariner eNTM", key: "eNtmSupport", width: 80 },
  { title: "AVCS Optimization Services", key: "avcsOptimization", width: 85 },
];

export const LicenceList: React.FC<IResourceComponentsProps> = () => {
  const { tableProps, sorters, tableQuery: { isLoading } } = useTable<ILicence>({
    sorters: {
      initial: [
        {
          field: "id",
          order: "desc",
        },
      ]
    }
  });

  const { triggerExport, isLoading: isLoadingExport } = useExport<ILicence>({
    mapData: (item) => {
      return {
        "UKHO Licence": item?.ukhoLicenceId,
        "Vessel IMO Number": item?.vessel?.imoNumber || "",
        "Vessel Name": item?.vessel?.name || "",
        "AVCS Service": item?.subscription?.metadataAvcs ? "yes" : "no",
        "ADP Service": item?.subscription?.metadataAdp ? "yes" : "no",
        "e-NP Service": item?.subscription?.metadataEnp ? "yes" : "no",
        "ARCS Service": item?.subscription?.metadataArcs ? "yes" : "no",
        ...Object.fromEntries(
          ROLLUP_PRODUCTS.map((p) => [
            p.title,
            isProductActive(
              productGroup(p.title),
              item?.subscription?.metadataOthers,
            )
              ? "yes"
              : "no",
          ]),
        ),
        "Shipco": item?.shipco?.registrationName || "",
        "Shipco Group": item?.shipcoGroup?.registrationName || "",
        "Subscription": item?.subscription?.subscriptionName || "",
      };
    },
    unparseConfig: {
      header: true,
      columns: [
        "UKHO Licence",
        "Vessel IMO Number",
        "Vessel Name",
        "AVCS Service",
        "ADP Service",
        "e-NP Service",
        "ARCS Service",
        "Standard Library Publications",
        "eMariner Planning Station",
        "eMariner eNTM",
        "AVCS Optimization Services",
        "Shipco",
        "Shipco Group",
        "Subscription",
      ],
    },
  });

  const { selectProps: imoSelectProps } = useSelect<IVessel>({
    resource: "vessel",
    optionLabel: "imoNumber",
    optionValue: "imoNumber",

    pagination: {
      mode: "server"
    }
  });

  const { selectProps: vesselSelectProps } = useSelect<IVessel>({
    resource: "vessel",
    optionLabel: "name",
    optionValue: "name",

    pagination: {
      mode: "server"
    }
  });

  const { selectProps: shipcoSelectProps } = useSelect<IShipco>({
    resource: "shipco",
    optionLabel: "registrationName",
    optionValue: "registrationName",

    pagination: {
      mode: "server"
    }
  });

  const columns: ColumnsType<ILicence> = [
    {
      title: "IMO Number",
      dataIndex: ["vessel", "imoNumber"],
      key: "vessel.imoNumber",
      width: 130,
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            mode="multiple"
            placeholder="Select IMO"
            {...imoSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "Vessel Name",
      dataIndex: ["vessel", "name"],
      key: "vessel.name",
      width: 180,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            mode="multiple"
            placeholder="Select Vessel"
            {...vesselSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "Shipco",
      dataIndex: ["shipco", "registrationName"],
      key: "shipco.registrationName",
      width: 200,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            mode="multiple"
            placeholder="Select Shipco"
            {...shipcoSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "AVCS Service",
      dataIndex: ["subscription", "metadataAvcs"],
      key: "subscription.metadataAvcs",
      width: 70,
      align: "center",
      render: (value) => (
        <TagField
          color={getBooleanTagColor(!!value)}
          value={value ? "yes" : "no"}
        />
      ),
    },
    {
      title: "ADP Service",
      dataIndex: ["subscription", "metadataAdp"],
      key: "subscription.metadataAdp",
      width: 70,
      align: "center",
      render: (value) => (
        <TagField
          color={getBooleanTagColor(!!value)}
          value={value ? "yes" : "no"}
        />
      ),
    },
    {
      title: "e-NP Service",
      dataIndex: ["subscription", "metadataEnp"],
      key: "subscription.metadataEnp",
      width: 70,
      align: "center",
      render: (value) => (
        <TagField
          color={getBooleanTagColor(!!value)}
          value={value ? "yes" : "no"}
        />
      ),
    },
    ...ROLLUP_PRODUCTS.map((p) => ({
      title: p.title,
      key: p.key,
      width: p.width,
      align: "center" as const,
      render: (_: unknown, record: ILicence) => {
        const active = isProductActive(
          productGroup(p.title),
          record?.subscription?.metadataOthers,
        );
        return (
          <TagField
            color={getBooleanTagColor(active)}
            value={active ? "yes" : "no"}
          />
        );
      },
    })),
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 120,
      render: (_, record) => (
        <Space>
          <EditButton hideText size="small" recordItemId={record.id} />
          <ShowButton hideText size="small" recordItemId={record.id} />
          <DeleteButton hideText size="small" recordItemId={record.id} />
        </Space>
      ),
    },
  ];

  return (
    <List
      breadcrumb={false}
      headerProps={{
        // onBack: () => window.history.back(),
        subTitle: "active licences",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
        extra: (
          <React.Fragment>
            <Space>
              <CreateButton />
              <ExportButton onClick={triggerExport} loading={isLoadingExport} />
            </Space>
          </React.Fragment>
        ),
        ghost: true,
        footer: (
          <Divider plain>
            © {new Date().getFullYear()} e-Mariner NavSolutions
          </Divider>
        ),
      }}
    >
      <Table
        {...tableProps}
        columns={columns}
        rowKey="id"
        size="middle"
        scroll={{ x: 1200 }}
        sticky
        pagination={{
          ...tableProps.pagination,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
        }}
      />
    </List>
  );
};
